import Vue from 'vue';
import Vuex from 'vuex';
import * as Enums from '@/enums.ts';
import * as Util from '@/util.ts';
import * as Constants from '@/constants.ts';
import storage from 'local-storage-fallback';
import router from '@/router.ts';

Vue.use(Vuex);

interface FeatureFlags {
  [key: string]: Enums.eFeatureFlagTypes;
}

/*
 Only works if you do not use Dates, functions, undefined, Infinity, RegExps, Maps, Sets, Blobs, FileLists, ImageDatas, sparse Arrays, Typed Arrays or other complex types within your object.
*/
const deepCopy = (object: any) => {
  return JSON.parse(JSON.stringify(object));
};


export const storeEventBus = new Vue();

interface State {
  initializationComplete: boolean;
  userId: string;
  isLoggedIn: boolean;
  currentClientUser: ClientUser | null;
  currentClientUserDoc: firebase.firestore.DocumentReference | null;
  currentDeliveryAddress: DeliveryAddress | null;
  stationId: number;
  stationCode: string;
  locationId: number;
  currentLocationDoc: firebase.firestore.DocumentReference;
  currentSubscriptionDoc: firebase.firestore.DocumentReference;
  currentUserEstablishmentExchangeDoc: firebase.firestore.DocumentReference | null;
  currentUserEstablishmentExchangeObject: UserEstablishmentExchangeObject;
  currentMenu: Menu;
  currentMenuItem: MenuItem;
  currentEstablishment: Establishment;
  currentLocation: EstablishmentLocation;
  currentSubscriptionData: Subscription;
  establishmentArray: Establishment[];
  locationArray: EstablishmentLocation[];
  menuArray: Menu[];
  menuCategoryArray: MenuCategory[];
  menuItemArray: MenuItem[];
  timeSlotArray: TimeSlot[];
  promotionArray: Promotion[];
  presetOptionGroupArray: OptionGroup[];
  priceDisplayFormat: string;
  toBeSentOrders: OrderItem[];
  lastSentOrders: OrderItem[];
  filters: FilterObject;
  logoUrl: string;
  moneySymbol: string;
  includeTaxWithPrices: boolean;
  showImagesAutomatically: boolean;
  bannedUsers: string[];
  referenceName: string;
  translationsWerePulled: boolean;
  locale: TranslationLocaleCode | null;
  appMode: Enums.eAppModes | null;
  featureFlags: FeatureFlags;
  isDarkTheme: boolean;
  serviceWorkerRegistation: any;
  menuSelectionPossible: boolean;
  stripeStyles: any;
  isKioskMode: boolean;
  contactEmail: string;
  contactNumber: number | null;
  showNavBar: boolean;
  locationsToPickFrom: EstablishmentLocation[];
  menusToPickFrom: Menu[];
  isDemoLocation: boolean;
  themeWasInitialized: boolean;
  gotAllResources: boolean;
  modalVisible: boolean;
  isOutOfDeliveryRange: boolean;
  currentDistanceFromLocation: number;
  takeoutAvailable: boolean;
  deliveryAvailable: boolean;
  tablesideAvailable: boolean;
  canOrderTakeoutForNowOrLater: boolean;
  canOrderDeliveryForNowOrLater: boolean;
  isWhiteLabel: boolean;
  isFirstTimeAtLocation: boolean;
  highestConfirmedAge: number;
  tablesideViewedCounts: Count;
  takeoutViewedCounts: Count;
  deliveryViewedCounts: Count;
  permalinkId: string;
  isEmbedded: boolean;
  requestedTime: number;
  cannotOrderTakeoutOrDelivery: boolean;
  isInEurope: boolean;
}

function initialState() {

  return {
    initializationComplete: false,
    userId: '',
    isLoggedIn: false,
    currentClientUser: null,
    currentClientUserDoc: null,
    currentDeliveryAddress: null,
    stationId: -1,
    stationCode: '',
    locationId: -1,
    currentLocationDoc: {} as firebase.firestore.DocumentReference,
    currentSubscriptionDoc: {} as firebase.firestore.DocumentReference,
    currentUserEstablishmentExchangeDoc: null,
    currentUserEstablishmentExchangeObject: deepCopy(Constants.EMPTY_USER_ESTABLISHMENT_ECHANGE_OBJ),
    currentMenu: deepCopy(Constants.EMPTY_MENU),
    currentMenuItem: deepCopy(Constants.EMPTY_MENU_ITEM),
    currentEstablishment: deepCopy(Constants.EMPTY_ESTABLISHMENT),
    currentLocation: deepCopy(Constants.EMPTY_LOCATION),
    currentSubscriptionData: deepCopy(Constants.EMPTY_SUBSCRIPTION),
    establishmentArray: [],
    locationArray: [],
    menuArray: [],
    menuCategoryArray: [],
    menuItemArray: [],
    timeSlotArray: [],
    promotionArray: [],
    presetOptionGroupArray: [],
    priceDisplayFormat: Enums.ePriceDisplayFormats.minDecimal,
    toBeSentOrders: [],
    lastSentOrders: [],
    filters: Constants.EMPTY_FILTER_OBJECT,
    logoUrl: '',
    moneySymbol: '$',
    includeTaxWithPrices: storage.getItem('includeTaxWithPrices') === 'true',
    showImagesAutomatically: storage.getItem('showImagesAutomatically') === null ? true : storage.getItem('showImagesAutomatically') === 'true', // True if unset.
    bannedUsers: [],
    referenceName: storage.getItem('referenceName') || '',
    translationsWerePulled: false,
    locale: storage.getItem('locale') || null,
    appMode: null,
    featureFlags: {
      serviceReview: Enums.eFeatureFlagTypes.demo,
      tablsidePayments: Enums.eFeatureFlagTypes.off,
    },
    isDarkTheme: false,
    serviceWorkerRegistation: null,
    menuSelectionPossible: false,
    isKioskMode: storage.getItem('kioskMode') === 'true',
    stripeStyles: {},
    contactEmail: '',
    contactNumber: null,
    showNavBar: false,
    locationsToPickFrom: [],
    menusToPickFrom: [],
    isDemoLocation: false,
    themeWasInitialized: false,
    gotAllResources: false,
    modalVisible: false,
    isOutOfDeliveryRange: false,
    currentDistanceFromLocation: -1,
    takeoutAvailable: false,
    deliveryAvailable: false,
    tablesideAvailable: false,
    canOrderTakeoutForNowOrLater: false,
    canOrderDeliveryForNowOrLater: false,
    isWhiteLabel: false,
    isFirstTimeAtLocation: false,
    highestConfirmedAge: Number(storage.getItem('highestConfirmedAge') || -1),
    tablesideViewedCounts: {},
    takeoutViewedCounts: {},
    deliveryViewedCounts: {},
    permalinkId: '',
    isEmbedded: false,
    requestedTime: 0,
    cannotOrderTakeoutOrDelivery: false,
    isInEurope: false,
  } as State;
}

export const store = new Vuex.Store({
  state: initialState(),
  mutations: {
    reset(state) {
      // acquire initial state
      const s: State = initialState();
      Object.keys(s).forEach((key: string) => {
        (state as any)[key] = (s as any)[key];
      });
    },
    setInitializationComplete(state, payload: boolean) { state.initializationComplete = payload; },
    setUserId(state, payload: string) { state.userId = payload; },
    setStationCode(state, payload: string) { state.stationCode = payload; },
    setCurrentClientUser(state, payload: ClientUser) {
      const clientUser = payload;
      const clientUserDoc = state.currentClientUserDoc as firebase.firestore.DocumentReference;
      state.currentClientUser = clientUser;

      // Make sure we have a reference name.
      const referenceName = clientUser.settings && clientUser.settings.referenceName || clientUser.displayName;
      if (referenceName) {
        store.commit('setReferenceName', referenceName);
      } else if (state.referenceName) { // If we have a reference name in storage use that as the reference name and save it to the client user account.
        clientUserDoc.update({
          ['settings.referenceName']: state.referenceName,
        })
          .catch((error: any) => {
            Util.showError('Could not save reference name. ' + error?.message);
          });
      }
      // Make sure we have a contact email.
      const contactEmail = clientUser.settings && clientUser.settings.contactEmail || clientUser.email;
      if (contactEmail) {
        store.commit('setContactEmail', contactEmail);
      } else if (state.contactEmail) { // If we have a email in storage use that as the reference name and save it to the client user account.
        clientUserDoc.update({
          ['settings.contactEmail']: state.contactEmail,
        })
          .catch((error: any) => {
            Util.showError('Could not save contact email. ' + error?.message);
          });
      }
      if (clientUser.deliveryAddress !== undefined) {
        store.commit('setCurrentDeliveryAddress', clientUser.deliveryAddress);
      }
      if (clientUser.settings.contactNumber !== undefined) {
        store.commit('setContactNumber', clientUser.settings.contactNumber);
      }
      if (clientUser.settings.includeTaxWithPrices !== undefined) {
        store.commit('setIncludeTaxWithPrices', clientUser.settings.includeTaxWithPrices);
      }
      if (clientUser.settings.showImagesAutomatically !== undefined) {
        store.commit('setShowImagesAutomatically', clientUser.settings.showImagesAutomatically);
      }
      if (clientUser.settings.locale !== undefined) {
        store.commit('setLocale', clientUser.settings.locale);
      }
      if (clientUser.highestConfirmedAge !== undefined) {
        store.commit('setHighestConfirmedAge', clientUser.highestConfirmedAge);
      }
    },
    setCurrentClientUserDoc(state, payload: firebase.firestore.DocumentReference) { state.currentClientUserDoc = payload; },
    setCurrentDeliveryAddress(state, payload: DeliveryAddress | null) {
      state.currentDeliveryAddress = payload;
      if (state.appMode === Enums.eAppModes.delivery && state.currentLocation.maximumDeliveryOrderDistance && payload && payload.latitude && payload.longitude) {
        const distance: number | null = Util.getDistance(payload.latitude, payload.longitude);
        if (distance) {
          state.currentDistanceFromLocation = Math.round(distance * 100) / 100;
          state.isOutOfDeliveryRange = distance > state.currentLocation.maximumDeliveryOrderDistance;
          if (state.isOutOfDeliveryRange) {
            Util.showError(Util.t?.isOutOfDeliveryRange.supplant([state.currentLocation.maximumDeliveryOrderDistance, distance]));
          }
        }
      }
    },
    setIncludeTaxWithPrices(state, payload: boolean) {
      Util.localStoreSetSimple('includeTaxWithPrices', String(payload));
      state.includeTaxWithPrices = payload;
    },
    setReferenceName(state, payload: string) {
      Util.localStoreSetSimple('referenceName', payload);
      state.referenceName = payload;
    },
    setShowImagesAutomatically(state, payload: boolean) {
      Util.localStoreSetSimple('showImagesAutomatically', String(payload));
      state.showImagesAutomatically = payload;
    },
    setEstablishmentArray(state, payload: Establishment[]) { state.establishmentArray = payload; },
    setLocationArray(state, payload: EstablishmentLocation[]) { state.locationArray = payload; },
    setMenuArray(state, payload: Menu[]) { state.menuArray = payload; },
    setMenuCategoryArray(state, payload: MenuCategory[]) {
      const highestValues: any = state.filters.highestValues;
      const lowestValues: any = state.filters.lowestValues;
      const selectedAttributes: any = state.filters.selectedAttributes;
      payload.forEach((mc: MenuCategory) => {
        mc.menuItems.forEach((mi: MenuItem) => {
          if (mi.docId) { // Ensuring it's not currently a document reference.
            Constants.FOOD_VALUES.forEach((val: string) => {
              switch (val) {
                case 'temperature':
                  highestValues[val] = Math.round(Number(Math.max(highestValues[val], Util.enumKeyNumValue(mi.temperature, Enums.eTemperatures))));
                  lowestValues[val] = Math.round(Number(Math.min(lowestValues[val], Util.enumKeyNumValue(mi.temperature, Enums.eTemperatures))));
                  break;
                default:
                  highestValues[val] = Math.round(Number(Math.max(highestValues[val], Number((mi as any)[val]))));
                  lowestValues[val] = Math.round(Number(Math.min(lowestValues[val], Number((mi as any)[val]))));
                  break;
              }
            });
            Constants.FOOD_ATTRIBUTES.forEach((a: string) => {
              const propertyName = `is${Util.capitalizeFirstLetter(a)}`; // Attribute properties are of the form isAttributeName
              if ((mi as any)[propertyName] != null) { // checking if null or undefined.
                selectedAttributes[a] = Enums.eAttributeSelectionTypes.unselected;
              }
            });
          }
        });
      });
      // Since price is represented in cents
      state.filters = {
        ...state.filters,
        highestValues,
        lowestValues,
        selectedAttributes,
      };
      state.menuCategoryArray = payload;
    },
    setMenuItemArray(state, payload: MenuItem[]) {
      payload.forEach((menuItem: MenuItem) => {
        menuItem.displayPrice = menuItem.price / Constants.CENTS_DOLLARS_MULTIPLIER;
        return menuItem;
      });
      state.menuItemArray = payload;
    },
    setTimeSlotArray(state, payload: TimeSlot[]) { state.timeSlotArray = payload; },
    setPromotionArray(state, payload: Promotion[]) { state.promotionArray = payload; },
    setPresetOptionGroupArray(state, payload: OptionGroup[]) { state.presetOptionGroupArray = payload; },
    setStationId(state, payload: number) { state.stationId = payload; },
    setLocationId(state, payload: number) { state.locationId = payload; },
    setCurrentLocationDoc(state, payload: firebase.firestore.DocumentReference) { state.currentLocationDoc = payload; },
    setCurrentSubscriptionDoc(state, payload: firebase.firestore.DocumentReference) { state.currentSubscriptionDoc = payload; },
    setCurrentUserEstablishmentExchangeDoc(state, payload: firebase.firestore.DocumentReference | null) { state.currentUserEstablishmentExchangeDoc = payload; },
    setCurrentMenu(state, payload: Menu) {
      state.currentMenu = payload;
      state.showNavBar = true;
    },
    setCurrentMenuItem(state, payload: MenuItem) {
      const formattedMenuItem: MenuItem = {
        ...Constants.EMPTY_MENU_ITEM,
        ...payload,
      };
      formattedMenuItem.combinedOptionGroups = formattedMenuItem.customOptionGroups.concat(formattedMenuItem.presetOptionGroups as OptionGroup[]),
        state.currentMenuItem = formattedMenuItem;
    },
    setIsLoggedIn(state, payload: boolean) { state.isLoggedIn = payload; },
    setPriceDisplayFormat(state, payload: string) { state.priceDisplayFormat = payload; },
    setFilterObject(state, payload: FilterObject) {
      state.filters = payload;
    },
    setLogoUrl(state, payload: string) { state.logoUrl = payload; },
    setCurrentEstablishment(state, payload: Establishment) {
      state.currentEstablishment = payload;
      if (payload.showPwaPromptImmediately) {
        Util.onShowPWAPrompt();
      }
    },
    setCurrentSubscriptionData(state, payload: Subscription) { state.currentSubscriptionData = payload; },
    setCurrentLocation(state, payload: EstablishmentLocation) {
      state.currentLocation = {
        ...Constants.EMPTY_LOCATION,
        ...payload,
      };
      const visitedLocationsString = storage.getItem('visitedLocations');
      if (visitedLocationsString) {
        const visitedLocations: number[] = JSON.parse(visitedLocationsString);
        if (visitedLocations.includes(payload.locationId)) {
          // Is a new user for this location in this case.
          visitedLocations.push(payload.locationId);
          state.isFirstTimeAtLocation = true;
          storage.setItem('visitedLocations', JSON.stringify(visitedLocations));
        }
      }

      const availabilities = Util.getAvailibilitiesForLocation(payload);
      store.commit('setTablesideAvailable', availabilities.tablesideAvailable);
      store.commit('setTakeoutAvailable', availabilities.takeoutAvailable);
      store.commit('setDeliveryAvailable', availabilities.deliveryAvailable);
      state.canOrderTakeoutForNowOrLater = availabilities.canOrderTakeoutForNowOrLater;
      state.canOrderDeliveryForNowOrLater = availabilities.canOrderDeliveryForNowOrLater;
      state.cannotOrderTakeoutOrDelivery = availabilities.cannotOrderTakeoutOrDelivery;
      state.locationId = payload.locationId;
      store.commit('setIsInEurope', ['Finland'].includes(payload.address.country));
    },
    setIsInEurope(state, payload: boolean) {
      state.isInEurope = payload;
      if (payload) {
        state.moneySymbol = '€';
      }
    },
    setTablesideAvailable(state, payload: boolean) { state.tablesideAvailable = payload; },
    setTakeoutAvailable(state, payload: boolean) { state.takeoutAvailable = payload; },
    setDeliveryAvailable(state, payload: boolean) { state.deliveryAvailable = payload; },
    setToBeSentOrders(state, payload: OrderItem[]) {
      state.toBeSentOrders = payload;
    },
    setlastSentOrders(state, payload: OrderItem[]) {
      state.lastSentOrders = payload;
    },
    setCurrentUserEstablishmentExchangeObject(state, payload: UserEstablishmentExchangeObject) {
      state.currentUserEstablishmentExchangeObject = payload;
    },
    setMoneySymbol(state, payload: string) { state.moneySymbol = payload; },
    setBannedUsers(state, payload: string[]) { state.bannedUsers = payload; },
    setTranslationsWerePulled(state, payload: any) { state.translationsWerePulled = payload; },
    setLocale(state, payload: TranslationLocaleCode) {
      Util.localStoreSetSimple('locale', payload);
      state.locale = payload;
      Util.changeLanguage();
      if (state.gotAllResources) {
        Util.getMenuTranslations();
      }
    },
    setAppMode(state, payload: Enums.eAppModes) { state.appMode = payload; },
    setIsDarkTheme(state, payload: boolean) { state.isDarkTheme = payload; },
    setServiceWorkerRegistation(state, payload: any) { state.serviceWorkerRegistation = payload; },
    setStripeStyles(state, payload: any) { state.stripeStyles = payload; },
    setIsKioskMode(state, payload: boolean) {
      storage.setItem('isKioskMode', payload.toString());
      state.isKioskMode = payload;
    },
    setContactEmail(state, payload: string) {
      Util.localStoreSetSimple('contactEmail', payload);
      state.contactEmail = payload;
    },
    setContactNumber(state, payload: number | null) {
      Util.localStoreSetSimple('contactNumber', String(payload) || '');
      state.contactNumber = payload;
    },
    setHighestConfirmedAge(state, payload: number) {
      Util.localStoreSetSimple('highestConfirmedAge', String(payload) || '-1');
      state.highestConfirmedAge = payload;
    },
    setLocationsToPickFrom(state, payload: EstablishmentLocation[]) { state.locationsToPickFrom = payload; },
    setMenusToPickFrom(state, payload: Menu[]) {
      if (payload.length > 1) {
        state.menuSelectionPossible = true;
      }
      state.menusToPickFrom = payload;
    },
    setIsDemoLocation(state, payload: boolean) { state.isDemoLocation = payload; },
    setThemeWasInitialized(state, payload: boolean) { state.themeWasInitialized = payload; },
    setGotAllResources(state, payload: boolean) {
      state.gotAllResources = payload;
      Util.getMenuTranslations();
    },
    setModalVisible(state, payload: boolean) { state.modalVisible = payload; },
    setTablesideViewedCounts(state, update: any) {
      state.tablesideViewedCounts = {
        ...state.tablesideViewedCounts,
        ...update,
      };
    },
    setTakeoutViewedCounts(state, update: any) {
      state.takeoutViewedCounts = {
        ...state.takeoutViewedCounts,
        ...update,
      };
    },
    setDeliveryViewedCounts(state, update: any) {
      state.deliveryViewedCounts = {
        ...state.deliveryViewedCounts,
        ...update,
      };
    },
    setPermalinkId(state, payload: string) {
      state.permalinkId = payload;
      Util.nonDebugLog('Permalink ID: ', payload || 'none');
      Util.localStoreSetSimple('lastPermalinkId', payload);
    },
    setIsEmbedded(state, payload: boolean) {
      state.isEmbedded = payload;
    },
    setRequestedTime(state, payload: number) {
      state.requestedTime = payload;
    },
  },
  getters: {
    cannotOrder(state): boolean {
      switch (state.appMode) {
        case Enums.eAppModes.takeout:
          return !state.canOrderTakeoutForNowOrLater;
          break;
        case Enums.eAppModes.delivery:
          return !state.canOrderDeliveryForNowOrLater;
          break;
      }
      return false;
    },
    pendingDeliveryOrders(state): DeliveryOrderObject[] {
      const verifiedAuthorizations = state.currentUserEstablishmentExchangeObject.verifiedAuthorizations || {};
      let GenericOrderObjects: DeliveryOrderObject[] = Object.values(state.currentUserEstablishmentExchangeObject.deliveryOrderCollection);
      GenericOrderObjects = GenericOrderObjects.filter((deliveryOrder) => {
        return deliveryOrder.status === Enums.eOrderStatuses.pending;
      }).map((deliveryOrder) => {
        // Check if the payment was verfied by stripe.
        const wasPaidFor = Boolean(deliveryOrder.paymentIntentId);
        deliveryOrder.notYetVerified = wasPaidFor && !Object.entries(verifiedAuthorizations).some(([paymentIntendId, verfiedStripeEvent]) => {
          return paymentIntendId === deliveryOrder.paymentIntentId && verfiedStripeEvent.amount === deliveryOrder.amount;
        });
        return deliveryOrder;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      });
      return GenericOrderObjects;
    },
    acceptedDeliveryOrders(state): DeliveryOrderObject[] {
      let GenericOrderObjects: DeliveryOrderObject[] = Object.values(state.currentUserEstablishmentExchangeObject.deliveryOrderCollection);
      GenericOrderObjects = GenericOrderObjects.filter((deliveryOrder) => {
        return deliveryOrder.status === Enums.eOrderStatuses.accepted;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      });
      return GenericOrderObjects;
    },
    dispatchedDeliveryOrders(state): DeliveryOrderObject[] {
      let GenericOrderObjects: DeliveryOrderObject[] = Object.values(state.currentUserEstablishmentExchangeObject.deliveryOrderCollection);
      GenericOrderObjects = GenericOrderObjects.filter((deliveryOrder) => {
        return deliveryOrder.status === Enums.eOrderStatuses.dispatched;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      });
      return GenericOrderObjects;
    },
    rejectedDeliveryOrders(state): DeliveryOrderObject[] {
      let GenericOrderObjects: DeliveryOrderObject[] = Object.values(state.currentUserEstablishmentExchangeObject.deliveryOrderCollection);
      GenericOrderObjects = GenericOrderObjects.filter((deliveryOrder) => {
        return deliveryOrder.status === Enums.eOrderStatuses.rejected;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      });
      return GenericOrderObjects;
    },
    pendingTakeoutOrders(state): TakeoutOrderObject[] {
      const verifiedAuthorizations = state.currentUserEstablishmentExchangeObject.verifiedAuthorizations || {};
      let GenericOrderObjects: TakeoutOrderObject[] = Object.values(state.currentUserEstablishmentExchangeObject.takeoutOrderCollection);
      GenericOrderObjects = GenericOrderObjects.filter((takeoutOrder) => {
        return takeoutOrder.status === Enums.eOrderStatuses.pending;
      }).map((takeoutOrder) => {
        // Check if the payment was verfied by stripe.
        const wasPaidFor = Boolean(takeoutOrder.paymentIntentId);
        takeoutOrder.notYetVerified = wasPaidFor && !Object.entries(verifiedAuthorizations).some(([paymentIntendId, verfiedStripeEvent]) => {
          return paymentIntendId === takeoutOrder.paymentIntentId && verfiedStripeEvent.amount === takeoutOrder.amount;
        });
        return takeoutOrder;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      });
      return GenericOrderObjects;
    },
    acceptedTakeoutOrders(state): TakeoutOrderObject[] {
      let GenericOrderObjects: TakeoutOrderObject[] = Object.values(state.currentUserEstablishmentExchangeObject.takeoutOrderCollection);
      GenericOrderObjects = GenericOrderObjects.filter((takeoutOrder) => {
        return takeoutOrder.status === Enums.eOrderStatuses.accepted;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      });
      return GenericOrderObjects;
    },
    readyForPickUpTakeoutOrders(state): TakeoutOrderObject[] {
      let GenericOrderObjects: TakeoutOrderObject[] = Object.values(state.currentUserEstablishmentExchangeObject.takeoutOrderCollection);
      GenericOrderObjects = GenericOrderObjects.filter((takeoutOrder) => {
        return takeoutOrder.status === Enums.eOrderStatuses.readyForPickUp;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      });
      return GenericOrderObjects;
    },
    rejectedTakeoutOrders(state): TakeoutOrderObject[] {
      let GenericOrderObjects: TakeoutOrderObject[] = Object.values(state.currentUserEstablishmentExchangeObject.takeoutOrderCollection);
      GenericOrderObjects = GenericOrderObjects.filter((takeoutOrder) => {
        return takeoutOrder.status === Enums.eOrderStatuses.rejected;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      });
      return GenericOrderObjects;
    },
    tablesidePendingOrders(state): OrderItem[] {
      return (Object.values(state.currentUserEstablishmentExchangeObject.tablesideOrderCollection) as GenericOrderObject[]).filter((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.status === Enums.eOrderStatuses.pending;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      }).flatMap((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.orderItems;
      }) || [];
    },
    tablesideAcceptedOrders(state): OrderItem[] {
      return (Object.values(state.currentUserEstablishmentExchangeObject.tablesideOrderCollection) as GenericOrderObject[]).filter((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.status === Enums.eOrderStatuses.accepted;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      }).flatMap((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.orderItems;
      }) || [];
    },
    tablesideRejectedOrders(state): OrderItem[] {
      return (Object.values(state.currentUserEstablishmentExchangeObject.tablesideOrderCollection) as GenericOrderObject[]).filter((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.status === Enums.eOrderStatuses.rejected;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      }).flatMap((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.orderItems;
      }) || [];
    },
    tablesideCancelledOrders(state): OrderItem[] {
      return (Object.values(state.currentUserEstablishmentExchangeObject.tablesideOrderCollection) as GenericOrderObject[]).filter((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.status === Enums.eOrderStatuses.cancelled;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      }).flatMap((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.orderItems;
      }) || [];
    },
    tablesideUnpaidOrders(state): OrderItem[] {
      return (Object.values(state.currentUserEstablishmentExchangeObject.tablesideOrderCollection) as GenericOrderObject[]).filter((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.status === Enums.eOrderStatuses.accepted && !tableSideGenericOrderObject.paymentIntentId;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      }).flatMap((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.orderItems;
      }) || [];
    },
    tablesidePaidOrders(state): OrderItem[] {
      return (Object.values(state.currentUserEstablishmentExchangeObject.tablesideOrderCollection) as GenericOrderObject[]).filter((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.status === Enums.eOrderStatuses.accepted && tableSideGenericOrderObject.paymentIntentId;
      }).sort((a: GenericOrderObject, b: GenericOrderObject) => {
        return (b.dateCreated as number) - (a.dateCreated as number); // Sort notifications by date in descending fashion.
      }).flatMap((tableSideGenericOrderObject: GenericOrderObject) => {
        return tableSideGenericOrderObject.orderItems;
      }) || [];
    },
    establishmentName(state): string {
      return state.currentEstablishment.name;
    },
  },
});
